/**
 * Projects API Client
 */

import { httpClient } from './client';
import { assertEntityId } from '../types/validation';

export interface IProject {
  readonly id: string;
  readonly name: string;
  readonly description?: string;
}

export interface ICreateProjectRequest {
  readonly name: string;
  readonly description?: string;
}

export interface IProjectsApiClient {
  listProjects(): Promise<IProject[]>;
  createProject(request: ICreateProjectRequest): Promise<IProject>;
}

type IProjectResponse = {
  readonly id: string | number;
  readonly name: string;
  readonly description?: string;
};

function normalizeProject(data: IProjectResponse): IProject {
  return {
    // Форму идентификатора клиент не выясняет: число и его строковая запись —
    // один и тот же идентификатор (dto-conventions-R04).
    id: assertEntityId(data.id, 'project.id'),
    name: data.name,
    description: data.description,
  };
}

function getErrorMessageFromResponseJson(
  data: unknown,
  fallbackMessage: string,
): string {
  if (typeof data !== 'object' || data === null) return fallbackMessage;
  if (!('message' in data)) return fallbackMessage;

  const message = (data as { readonly message?: unknown }).message;
  if (typeof message !== 'string') return fallbackMessage;
  if (message.trim().length === 0) return fallbackMessage;
  return message;
}

export class ProjectsApiClient implements IProjectsApiClient {
  async listProjects(): Promise<IProject[]> {
    const response = await httpClient.get<IProjectResponse[]>('/projects');
    return response.data.map(normalizeProject);
  }

  async createProject(request: ICreateProjectRequest): Promise<IProject> {
    try {
      const response = await httpClient.post<IProjectResponse>('/projects', request);
      return normalizeProject(response.data);
    } catch (error: unknown) {
      if (error !== null && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown; statusText?: string } };
        const errorMessage = getErrorMessageFromResponseJson(
          axiosError.response?.data,
          axiosError.response?.statusText ?? 'Unknown error',
        );
        throw new Error(`Failed to create project: ${errorMessage}`);
      }
      throw error;
    }
  }
}

// Singleton instance
let projectsApiClient: ProjectsApiClient | null = null;

export function getProjectsApiClient(): ProjectsApiClient {
  projectsApiClient ??= new ProjectsApiClient();
  return projectsApiClient;
}

